/**
 * LightboxLeaderboardPlugin
 *
 * This plugin handles the rendering and refreshing of a leaderboard ad that appears
 * within the lightbox gallery on ucp-desktop/ucp-mobile.
 *
 * ## High-Level Overview:
 * When a user clicks an image in an Article or an InfoBox, a lightbox modal opens
 * (managed by UCP). At this point, a
 * `.gallery-leaderboard` container is injected into the DOM. This plugin then
 * injects a `#lightbox_leaderboard` ad slot into that container.
 *
 * Plugin also ensures that the floor_adhesion unit is hidden
 * while the lightbox is open and shown again when it closes.
 *
 * ## Responsibilities:
 * - Inject the lightbox_leaderboard into the .gallery-leaderboard on open.
 * - Run Prebid to request bids and refresh the ad.
 * - Manage refreshLock state to avoid excessive requests.
 * - Temporarily hide the floor adhesion ad while the lightbox is active.
 *
 * ## Public API:
 * This plugin exposes 3 public methods via API for external usage.
 * Examples of usage:
 * onLightboxOpen():
 * @link https://github.com/Wikia/unified-platform/blob/3a2e274355011d27294c2f0ba6167adde00e79dc/skins/FandomMobile/scripts/lightbox/lightboxAds.ts#L25
 * onLightboxClose():
 * @link https://github.com/Wikia/unified-platform/blob/3a2e274355011d27294c2f0ba6167adde00e79dc/skins/FandomMobile/scripts/lightbox/lightboxAds.ts#L55
 * onLightboxChange():
 * @link https://github.com/Wikia/unified-platform/blob/3a2e274355011d27294c2f0ba6167adde00e79dc/skins/FandomMobile/scripts/lightbox/lightboxAds.ts#L40
 */

import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { AdNode } from "../../core/AdNode";
import type { AdNodeManager } from "../../core/AdNodeManager";
import type { AdEngine } from "../../core/AdEngine";

interface LightboxLeaderboardPluginAPI {
  onLightboxOpen: () => void;
  onLightboxClose: () => void;
  onLightboxChange: () => void;
}

export class LightboxLeaderboardPlugin extends AdEnginePlugin<LightboxLeaderboardPluginAPI> {
  private adEngine?: AdEngine;
  private adNodeManager?: AdNodeManager;
  private refreshLock = false;
  private galleryLeaderboard: HTMLElement | null = null;
  private lightboxLeaderboardAdNode?: AdNode;

  /**
   * Prevents ad refreshes for a short time to avoid excessive requests
   * @param duration Duration in milliseconds, defaults to 2000ms
   */
  private setRefreshLock(duration = 2000) {
    this.refreshLock = true;

    setTimeout(() => {
      this.refreshLock = false;
    }, duration);
  }

  private waitForFloorWrapper(callback: (element: HTMLElement) => void) {
    const element = document.getElementById("floor_adhesion_wrapper");
    if (element) {
      callback(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.getElementById("floor_adhesion_wrapper");
      if (element) {
        observer.disconnect();
        callback(element);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  private toggleFloorAdhesion(shouldHide: boolean) {
    this.waitForFloorWrapper((element: HTMLElement) => {
      element.classList.toggle("hide-under-lightbox", shouldHide);
    });
  }

  hooks: Hooks = {
    onAfterAdNodeManagerInit: (adNodeManager) => {
      this.adNodeManager = adNodeManager;
    },
    onAdEngineInit: (adEngine) => {
      this.adEngine = adEngine;
    },
  };

  /**
   * Grabs the .gallery-leaderboard container and injects a new ad div into it if needed
   */
  private getOrCreateLightboxLeaderboard(): HTMLElement | null {
    if (!this.galleryLeaderboard) return null;

    let lightboxLeaderboard = document.getElementById(
      "lightbox_leaderboard",
    ) as HTMLElement;

    if (!lightboxLeaderboard) {
      lightboxLeaderboard = document.createElement("div");
      lightboxLeaderboard.id = "lightbox_leaderboard";
      this.galleryLeaderboard.prepend(lightboxLeaderboard);

      this.lightboxLeaderboardAdNode ??= this.adNodeManager?.createAdNode(
        "lightbox_leaderboard",
      );
    }

    return lightboxLeaderboard;
  }

  private renderOrRefreshAd() {
    if (this.refreshLock) return;

    const lightboxLeaderboard = this.getOrCreateLightboxLeaderboard();
    if (!lightboxLeaderboard) return;

    this.toggleFloorAdhesion(true);
    lightboxLeaderboard.classList.remove("is-loading");

    const innerWrapper = document?.getElementsByClassName(
      "lightbox-wrapper-inner",
    )[0];
    innerWrapper?.classList?.add("with-ad");

    this.bidAndRefreshLightboxLeaderboardAdNode();
  }

  private async bidAndRefreshLightboxLeaderboardAdNode() {
    this.setRefreshLock();

    if (!this.lightboxLeaderboardAdNode) return;

    this.adEngine?.requestRender([this.lightboxLeaderboardAdNode], true);
  }

  api = {
    onLightboxOpen: () => {
      this.galleryLeaderboard = document.querySelector(".gallery-leaderboard");
      if (!this.galleryLeaderboard) return;

      this.renderOrRefreshAd();
    },

    onLightboxClose: () => {
      this.galleryLeaderboard = null;
      this.toggleFloorAdhesion(false);
    },

    onLightboxChange: () => {
      this.renderOrRefreshAd();
    },
  };
}
